// ============================================================================
// PROJECT HOOKS - API Integration for Projects
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { Project, CreateProjectInput, UpdateProjectInput } from '../types';

// ============================================================================
// LIST PROJECTS
// ============================================================================

export interface BreakGlassContext {
  category: string;
  justification: string;
}

export function breakGlassHeaders(ctx?: BreakGlassContext | null): Record<string, string> {
  if (!ctx) return {};
  return {
    'X-Break-Glass-Category': ctx.category,
    'X-Break-Glass-Justification': ctx.justification,
  };
}

interface UseProjectsFilters {
  status?: 'active' | 'archived';
  breakGlass?: BreakGlassContext | null;
}

export const useProjects = (filters?: UseProjectsFilters) => {
  const { breakGlass, ...queryFilters } = filters || {};
  return useQuery<Project[]>({
    queryKey: ['projects', queryFilters, !!breakGlass],
    queryFn: async () => {
      const response = await api.get('/api/projects', {
        params: queryFilters,
        headers: breakGlassHeaders(breakGlass),
      });
      const projects = response.data.projects || [];
      // Filter out any undefined/invalid items
      return Array.isArray(projects) ? projects.filter((p: any) => p && p.projectId) : [];
    },
    staleTime: 30000,
  });
};

// ============================================================================
// GET SINGLE PROJECT
// ============================================================================

export const useProject = (projectId: string) => {
  return useQuery<Project>({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const response = await api.get(`/api/projects/${projectId}`);
      return response.data.project || response.data;
    },
    enabled: !!projectId,
  });
};

// ============================================================================
// CREATE PROJECT
// ============================================================================

export const useCreateProject = () => {
  const queryClient = useQueryClient();

  return useMutation<Project, Error, CreateProjectInput>({
    mutationFn: async (data: CreateProjectInput) => {
      const response = await api.post('/api/projects', data);
      return response.data.project || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

// ============================================================================
// UPDATE PROJECT
// ============================================================================

export const useUpdateProject = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdateProjectInput>({
    mutationFn: async (updates: UpdateProjectInput) => {
      await api.put(`/api/projects/${projectId}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

// ============================================================================
// DELETE PROJECT
// ============================================================================

export const useDeleteProject = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { projectId: string; breakGlass?: BreakGlassContext | null }>({
    mutationFn: async ({ projectId, breakGlass }) => {
      await api.delete(`/api/projects/${projectId}`, { headers: breakGlassHeaders(breakGlass) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

// ============================================================================
// PROJECT SESSIONS
// ============================================================================

interface UseProjectSessionsOptions {
  limit?: number;
}

export interface ProjectSession {
  sessionId: string;
  customerName?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  phase?: string;
  contentClearedAt?: string;
}

export const useProjectSessions = (projectId: string, options?: UseProjectSessionsOptions) => {
  return useQuery<ProjectSession[]>({
    queryKey: ['project-sessions', projectId, options],
    queryFn: async () => {
      const response = await api.get(`/api/projects/${projectId}/sessions`, { params: options });
      const sessions: ProjectSession[] = response.data.sessions || [];
      return sessions.filter(s => s.status !== 'archived');
    },
    enabled: !!projectId,
  });
};

// ============================================================================
// RENAME SESSION
// ============================================================================

export const useRenameSession = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { sessionId: string; customerName: string }>({
    mutationFn: async ({ sessionId, customerName }) => {
      await api.patch(`/api/sessions/${sessionId}/name`, { customerName });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-sessions', projectId] });
    },
  });
};

// ============================================================================
// UNLINK SESSION FROM PROJECT
// ============================================================================

export const useUnlinkSession = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (sessionId: string) => {
      await api.delete(`/api/projects/${projectId}/sessions/${sessionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-sessions', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
};

// ============================================================================
// PROJECT ACCESS CONTROL (ACL)
// ============================================================================

export interface ProjectACLEntry {
  userId: string;
  email: string;
  accessLevel: 'read' | 'write';
  grantedAt: string;
  grantedBy: string;
  expiresAt?: string;
}

export interface ProjectACLResponse {
  acl: ProjectACLEntry[];
  ownerId: string;
  ownerEmail: string;
}

export interface AddACLEntryInput {
  email: string;
  accessLevel: 'read' | 'write';
  expiresAt?: string;
}

export interface UserSearchResult {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
}

/**
 * Get Project ACL
 */
export const useProjectACL = (projectId: string) => {
  return useQuery<ProjectACLResponse>({
    queryKey: ['project-acl', projectId],
    queryFn: async () => {
      const response = await api.get(`/api/projects/${projectId}/acl`);
      return {
        acl: response.data.acl || [],
        ownerId: response.data.ownerId,
        ownerEmail: response.data.ownerEmail
      };
    },
    enabled: !!projectId,
  });
};

/**
 * Add ACL Entry
 */
export const useAddACLEntry = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, AddACLEntryInput>({
    mutationFn: async (data: AddACLEntryInput) => {
      await api.post(`/api/projects/${projectId}/acl`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-acl', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
};

/**
 * Remove ACL Entry
 */
export const useRemoveACLEntry = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (userId: string) => {
      await api.delete(`/api/projects/${projectId}/acl/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-acl', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
};

export interface DeletionResult {
  tablesCleared: string[];
  s3ObjectsDeleted: number;
  deviceImagesS3Deleted: number;
  graphNodesDeleted: number;
  vectorsDeleted: number;
  auditEventsDeleted: number;
  errors: string[];
}

/**
 * Delete all project content (sessions, KB, S3, graph). Project record is preserved.
 */
export const useDeleteProjectContent = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<DeletionResult, Error, { breakGlass?: BreakGlassContext | null } | undefined>({
    mutationFn: async (args) => {
      const resp = await api.delete(`/api/projects/${projectId}/content`, {
        headers: breakGlassHeaders(args?.breakGlass),
      });
      return resp.data.result as DeletionResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-sessions', projectId] });
    },
  });
};

export interface ContentAuditStore {
  key: string;
  label: string;
  count: number;
  note?: string;
}

export interface ContentAuditResult {
  sessionId: string;
  verifiedAt: string;
  stores: ContentAuditStore[];
}

export const useSessionContentAudit = (sessionId: string, enabled: boolean) => {
  return useQuery<ContentAuditResult>({
    queryKey: ['session-content-audit', sessionId],
    queryFn: async () => {
      const resp = await api.get(`/api/sessions/${sessionId}/content/audit`);
      return resp.data.result as ContentAuditResult;
    },
    enabled: !!sessionId && enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

/**
 * Delete all session content (DynamoDB, S3, graph).
 */
export const useDeleteSessionContent = (sessionId: string) => {
  return useMutation<DeletionResult, Error>({
    mutationFn: async () => {
      const resp = await api.delete(`/api/sessions/${sessionId}/content`);
      return resp.data.result as DeletionResult;
    },
  });
};

/**
 * Search Users in Organization
 */
export const useSearchUsers = (projectId: string, searchQuery: string) => {
  return useQuery<UserSearchResult[]>({
    queryKey: ['users-search', projectId, searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];
      const response = await api.get(`/api/projects/${projectId}/users/search`, {
        params: { q: searchQuery }
      });
      return response.data.users || [];
    },
    enabled: searchQuery.length >= 2,
    staleTime: 30000, // 30 seconds
  });
};
